import React, { useState, useEffect } from 'react';

/**
 * BetaWelcomeModal - Welcome modal for first-time visitors
 * 
 * Purpose: Reassure visitors, set expectations, invite action without hype
 * Design: Clean, trustworthy, Apple-inspired aesthetics
 * 
 * Created: February 4, 2026
 */
export default function BetaWelcomeModal({ onClose, onJoinBeta }) {
  const [isVisible, setIsVisible] = useState(false);
  
  // Animate in on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);
  
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation
  };
  
  const handleJoinBeta = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      if (onJoinBeta) onJoinBeta();
    }, 300);
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative w-full max-w-lg transform transition-all duration-300 ${
          isVisible 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Card */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl border border-slate-700/50 shadow-2xl overflow-hidden">
          
          {/* Top Accent Bar */}
          <div className="h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />
          
          {/* Content */}
          <div className="p-8">
            
            {/* Beta Badge */}
            <div className="flex justify-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium">
                <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                Beta Preview
              </span>
            </div>
            
            {/* Title */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
              We are in beta
            </h2>
            <p className="text-slate-400 text-center mb-6">
              Here is what that means
            </p>
            
            {/* Body Content */}
            <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
              <p>
                <span className="text-white font-medium">AI Doctor Ben is early, but already useful.</span> You can upload your own lab results, see timelines and patterns, and get multi-perspective explanations – all stored privately on your device.
              </p>
              
              <p className="text-slate-400">
                Features may change quickly, and you might notice the occasional rough edge; your feedback helps us improve.
              </p>
              
              {/* Medical Disclaimer - Subtle but clear */}
              <div className="mt-4 p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                <p className="text-slate-400 text-sm">
                  <span className="text-amber-400">⚕️</span> We are a general health and education tool, not a doctor or diagnosis service. Always speak to a qualified clinician about your own medical decisions.
                </p>
              </div>
            </div>
            
            {/* Buttons */}
            <div className="mt-8 space-y-3">
              {/* Primary CTA */}
              <button
                onClick={handleClose}
                className="w-full py-3.5 px-6 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
                data-testid="beta-welcome-continue-btn"
              >
                Continue to site
              </button>
              
              {/* Secondary Link */}
              <button
                onClick={handleJoinBeta}
                className="w-full py-2 text-slate-400 hover:text-emerald-400 text-sm transition-colors flex items-center justify-center gap-2"
                data-testid="beta-welcome-join-btn"
              >
                Want to help shape AI Doctor Ben? 
                <span className="text-emerald-400 font-medium">Join the beta list →</span>
              </button>
            </div>
          </div>
          
          {/* Bottom subtle branding */}
          <div className="px-8 pb-6 pt-2 border-t border-slate-800/50">
            <p className="text-xs text-slate-500 text-center">
              Your privacy matters. Health records stay on your device.
            </p>
          </div>
        </div>
        
        {/* Close button (subtle, top right) */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-700/50 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
